import React, { useCallback, useEffect } from "react";
import { Badge, ButtonGroup, EditNationalId, EditPassport, EditableInput, LabelValue, Toast, TravellerSearch, Avatar } from "../../../../../../../../Components";
import { useClientSettings } from "../../../../../../../../Hooks/useClientSettings";
import useUserProfile from "../../../../../../../../Hooks/useUserProfile";
import { limitPhoneDigits, validatePhoneNumberDetailed } from "../../../../../../../../Utils/utils";
import apis from "../../../../../../../../Utils/apis";
import { get, patch } from "../../../../../../../../Utils/axios";
import { FiMail } from "react-icons/fi";
import { useUserConfig } from "../../../../../../../../Hooks/useUserConfig";
import { TravellerCard } from "./card";
import EditDob from "../../../../../../../../Components/EditDob";

function Travellers({ airlineCode, mandatoryField = {}, errors = [], resetErrors = () => {}, max, passengers = {}, setPassengers = () => {}, requestId }) {
	const { isEnabled } = useClientSettings();
	const { checkUserPermission } = useUserConfig();
	const allowTravellersSelection = isEnabled("ORGANIZATION") && checkUserPermission("TA");
	const { profile, reload } = useUserProfile();
	const { fName, lName, phone, email, dob, gender, photo, identification, phoneCode } = profile;

	const addTraveller = useCallback((traveller) => {
		let alreadySelected = false;
		setPassengers((prev) => {
			alreadySelected = prev.some((p) => `${p.id}|${p.isGuest}` === `${traveller.id}|${traveller.isGuest}`);
			return alreadySelected ? prev : [...prev, traveller];
		});

		if (alreadySelected) {
			return Toast.error("You have already selected this traveller once");
		}

		resetErrors();
	}, [resetErrors, setPassengers]);

	const updateTraveller = useCallback((traveller) => {
		setPassengers((prev) => prev.map((p) => (`${p.id}|${p.isGuest}` === `${traveller.id}|${traveller.isGuest}` ? traveller : p)));
		resetErrors();
	}, [resetErrors, setPassengers]);

	const removeTraveller = useCallback((id, isGuest) => {
		setPassengers((prev) => prev.filter((p) => `${p.id}|${p.isGuest}` !== `${id}|${isGuest}`));
		resetErrors();
	}, [resetErrors, setPassengers]);

	const fetchRequestDetails = useCallback((requestId) => {
		get(apis.getRequestorDetails, { requestId }, (r, e) => {
			if (r) {
				addTraveller(r);
			}
		});
	}, [addTraveller]);

	useEffect(() => {
		if (allowTravellersSelection) {
			setPassengers([]);
		} else {
			setPassengers([profile]);
		}
	}, [allowTravellersSelection, profile, setPassengers]);

	useEffect(() => {
		//If booking is done against a request, automatically add the requestor in travellers
		if (requestId) {
			fetchRequestDetails(requestId);
		}
	}, [fetchRequestDetails, requestId]);

	if (allowTravellersSelection) {
		return (
			<>
				{passengers.length < max && <TravellerSearch mandatoryField={mandatoryField} onSelect={addTraveller} selected={passengers} />}
				{requestId && passengers.length === max && <Badge variant="warning">Travellers were automatically added from the request</Badge>}
				{passengers.map(({ id, isGuest }, i) => (
					<TravellerCard key={i} mandatoryField={mandatoryField} id={id} isGuest={isGuest} removeTraveller={removeTraveller} airlineCode={airlineCode} updateTraveller={updateTraveller} resetErrors={resetErrors} />
				))}

				{errors.length > 0 && (
					<div className="flex flex-wrap gap-2 my-4">
						{errors.map((error, i) => (
							<Badge key={i} variant="danger">
								{`${i + 1}) ${error}`}
							</Badge>
						))}
					</div>
				)}
			</>
		);
	}

	const onUpdate = async (obj) => {
		const dismiss = Toast.loading("Updating...");
		if (Object.prototype.hasOwnProperty.call(obj, "phone")) {
			const region = Object.prototype.hasOwnProperty.call(obj, "phoneCode") ? obj.phoneCode : phoneCode;
			const phoneResult = await validatePhoneNumberDetailed(obj.phone, region);
			if (!phoneResult.ok) {
				dismiss();
				return Toast.error(phoneResult.message);
			}
		}
		patch(apis.updateMyProfile, obj, (r, e) => {
			if (r) {
				reload();
				dismiss();
			} else if (e) {
				console.log(e);
				dismiss();
				Toast.handleError(e);
			}
		});
	};

	return (
		<>
			<div className="border rounded-md fade-in-up overflow-hidden">
				{/* Header: identity + contact — wraps on small screens, consistent with TravellerCard */}
				<div className="p-3 md:p-4 bg-light rounded-t-md flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="min-w-0 flex-1 flex items-center gap-2">
						<Avatar photo={photo} className="w-6 h-6 shrink-0" />
						<h5 className="font-semibold text-dark truncate">
							{fName} {lName}
						</h5>
					</div>
					<div className="flex items-center gap-2 text-label min-w-0 truncate" title={email}>
						<FiMail className="shrink-0" aria-hidden />
						<span className="truncate">{email}</span>
					</div>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-3 md:p-4">
					<LabelValue label="Phone">
						<EditableInput type="number" required sanitizeValue={limitPhoneDigits} onUpdate={(phone) => onUpdate({ phone })} value={phone} label="Phone Number" />
						{phone}
					</LabelValue>
					<LabelValue label="Gender">
						<ButtonGroup
							size="xs"
							selected={{
								label: gender,
								value: gender,
							}}
							onClick={(obj) => onUpdate({ gender: obj.value })}
							options={[
								{ label: "Male", value: "MALE" },
								{ label: "Female", value: "FEMALE" },
							]}
						/>
					</LabelValue>
					{mandatoryField.dobValidation && (
						<LabelValue label="Date of Birth">
							<EditDob dob={dob} reload={reload} />
						</LabelValue>
					)}
					{mandatoryField.passportValidation && (
						<LabelValue label="Passport (Required)">
							<EditPassport reload={reload} identification={identification} photo={photo} />
						</LabelValue>
					)}
					{mandatoryField.nationalityValidation && (
						<LabelValue label="Government ID (Required)">
							<EditNationalId reload={reload} identification={identification} />
						</LabelValue>
					)}
				</div>
			</div>
			{errors.length > 0 && (
				<div className="flex flex-wrap gap-2 my-4 text-sm">
					{errors.map((error, i) => (
						<Badge key={i} variant="danger">
							{`${i + 1}) ${error}`}
						</Badge>
					))}
				</div>
			)}
		</>
	);
}

export default Travellers;
